import React, {
  useCallback,
  useEffect,
  useState
} from 'react';

import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation
} from 'react-router-dom';

import axios from 'axios';
import { LogOut } from 'lucide-react';

import {
  SettingsProvider,
  useSettings
} from './contexts/SettingsContext';

import {
  WhatsAppProvider
} from './contexts/WhatsAppContext';

import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import Leads from './pages/Leads';
import Clients from './pages/Clients';
import AddClient from './pages/AddClient';
import ClientDetails from './pages/ClientDetails';
import RatingChecklist from './pages/RatingChecklist';
import ServiceSteps from './pages/ServiceSteps';
import Remarketing from './pages/Remarketing';
// CRM_PROMOTIONS_FRONT_V1
import Promotions from './pages/Promotions';
// FINANCEIRO_RATING_FRONT_V1
import Financeiro from './pages/Financeiro';
import CRM from './pages/CRM';
import PixFacilitado from './pages/PixFacilitado';
import Settings from './pages/SettingsSafe';
import PainelUsers from './pages/PainelUsers';
import Login from './pages/Login';
import PartnerClients from './pages/PartnerClients';
import PartnerIntake from './pages/PartnerIntake';

axios.defaults.withCredentials = true;

const API = (
  process.env.REACT_APP_API_URL ||
  '/api'
).replace(/\/$/, '');

function Painel({ onLogout }) {
  const { darkMode } = useSettings();

  return (
    <div className={
      `flex min-h-screen ${
        darkMode
          ? 'bg-gray-900'
          : 'bg-gray-100'
      }`
    }>
      <Sidebar />

      <main className="flex-1 min-w-0">
        <div className="flex justify-end px-6 pt-4">
          <button
            onClick={onLogout}
            className={
              `${
                darkMode
                  ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
              } flex items-center gap-2 border rounded-lg px-3 py-2 text-sm font-medium`
            }
          >
            <LogOut size={17} />
            Sair
          </button>
        </div>

        <div className="p-6 pt-3">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/leads" element={<Leads />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/partner-clients" element={<PartnerClients />} />
            <Route path="/clients/add" element={<AddClient />} />
            <Route path="/clients/:id" element={<ClientDetails />} />
            <Route path="/clients/:leadId/checklist" element={<RatingChecklist />} />
            <Route path="/clients/:leadId/steps" element={<ServiceSteps />} />
            <Route path="/production-line" element={<CRM />} />
            <Route path="/pix-facilitado" element={<PixFacilitado />} />
            <Route path="/remarketing" element={<Remarketing />} />
            <Route path="/promotions" element={<Promotions />} />
            <Route path="/financial" element={<Financeiro />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/settings/users" element={<PainelUsers />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

function AppContent() {
  const location = useLocation();

  // FINANCY_LINK_PUBLICO_APP
  const paginaPublicaParceiro =
    /^\/coleta-parceiro\/[^/]+\/?$/.test(
      location.pathname
    ) ||
    /^\/coleta-financy\/?$/.test(
      location.pathname
    );

  const [usuario, setUsuario] = useState(null);
  const [verificando, setVerificando] = useState(true);

  const verificarSessao = useCallback(async () => {
    try {
      const response = await fetch(
        `${API}/auth/me`,
        {
          credentials: 'include'
        }
      );

      const data = await response.json();

      if (
        response.ok &&
        data.authenticated
      ) {
        setUsuario(data.usuario);
      } else {
        setUsuario(null);
      }
    } catch (error) {
      setUsuario(null);
    } finally {
      setVerificando(false);
    }
  }, []);

  useEffect(() => {
    if (paginaPublicaParceiro) {
      setVerificando(false);
      return;
    }

    verificarSessao();
  }, [
    verificarSessao,
    paginaPublicaParceiro
  ]);

  const sair = async () => {
    try {
      await fetch(
        `${API}/auth/logout`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );
    } finally {
      setUsuario(null);
    }
  };

  if (paginaPublicaParceiro) {
    return (
      <Routes>
        <Route
          path="/coleta-parceiro/:token"
          element={<PartnerIntake />}
        />

        <Route
          path="/coleta-financy"
          element={<PartnerIntake />}
        />
      </Routes>
    );
  }

  if (verificando) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-4 border-gray-700 border-t-blue-500 animate-spin" />
      </div>
    );
  }

  if (!usuario) {
    return (
      <Login
        onLogin={data =>
          setUsuario(data.usuario)
        }
      />
    );
  }

  return <Painel onLogout={sair} />;
}

function App() {
  return (
    <SettingsProvider>
      <WhatsAppProvider>
        <Router>
          <AppContent />
        </Router>
      </WhatsAppProvider>
    </SettingsProvider>
  );
}

export default App;
